import { useMemo } from 'react';
import {
    SupportCareTeamContactDetailsUpsert,
    SupportCareTeamMemberDetails,
} from '../../../../primary-record-types/apiTypes/medicalProfileService';
import { getIntl } from '../../../../utils/intl';
import {
    ContactDetailField,
    nameOrCompanyRequiredCustomValidators,
    useGetRenderContactDetailsFormFields,
} from '../../ContactDetailsForm';
import { SupportFormValue } from '../types';
import { defaultSupportValues, supportTypeSelectOptions } from '../utils';
import CareTeamTypeSelect from '../CareTeamTypeSelect';
import { SplitFormRow } from '../../../v2BaseComponents/Form';
import { Field, useFormikContext } from 'formik';
import { FormikInputs } from '../../../v2BaseComponents/Inputs';
import { AVATAR_UPLOAD_QUALITY } from '../../../../utils/constants';
import React from 'react';
import { FormStack } from '../../../v2BaseComponents/Stack';
import { CareTeamMemberSource, getCareTeamMemberAvatarUrl } from '../../../../utils/careTeamUtils';

const intl = getIntl('SupportFields', {
    submit: 'Submit',
    photo: 'Photo',
    isActive: 'Is Active',
    supportType: 'Support Type',
    selectSupportType: 'Select Support Type',
    otherTypeDescription: 'Please specify',
});

const contactDetailsFields: ContactDetailField[] = [
    'firstName',
    'lastName',
    'company',
    'description',
    'phoneNumber',
    'faxNumber',
    'email',
    'role',
    'address1',
    'address2',
    'city',
    'state',
    'zipCode',
    'url',
];

export const useSupportContactDetailsFields = (isDisabled?: boolean) =>
    useGetRenderContactDetailsFormFields({
        isDisabled: isDisabled,
        fields: contactDetailsFields,
        fieldNamePrefix: 'contactDetails',
        customValidators: nameOrCompanyRequiredCustomValidators,
    });

export const useSupportInitialValues = (
    initialValues?: SupportCareTeamMemberDetails<SupportCareTeamContactDetailsUpsert>,
) =>
    useMemo<SupportFormValue>(() => {
        if (!initialValues) return defaultSupportValues;

        return {
            ...initialValues,
            avatar: null,
        };
    }, [initialValues]);

export type SupportFieldsProps = {
    careTeamMemberId?: string;
    isDisabled?: boolean;
    source: CareTeamMemberSource;
    values: SupportFormValue;
};

const SupportFields: React.FC<SupportFieldsProps> = ({ careTeamMemberId, isDisabled, source }) => {
    const { renderFields } = useSupportContactDetailsFields(isDisabled);
    const { values } = useFormikContext<SupportFormValue>();

    const showOtherInput = values.metadata?.supportCareTeamMemberType === 'other';

    return (
        <FormStack>
            <CareTeamTypeSelect value="support" />
            <SplitFormRow>
                <Field
                    name="metadata.supportCareTeamMemberType"
                    component={FormikInputs.Select}
                    selectOptions={supportTypeSelectOptions}
                    title={intl('supportType')}
                    allowEmpty
                    placeholder={intl('selectSupportType')}
                />
                {showOtherInput && (
                    <Field
                        name="metadata.otherTypeDescription"
                        component={FormikInputs.Input}
                        title={intl('otherTypeDescription')}
                    />
                )}
            </SplitFormRow>
            <Field
                name="avatar"
                component={FormikInputs.TempAndExistingImagePicker}
                existingAvatarUrl={getCareTeamMemberAvatarUrl(source, careTeamMemberId)}
                title={intl('photo')}
                imagePickerOptions={{
                    allowsEditing: true,
                    aspect: [1, 1],
                    quality: AVATAR_UPLOAD_QUALITY,
                }}
                sizeVariant="avatar"
            />
            <SplitFormRow>
                <Field name="metadata.isActive" component={FormikInputs.Checkbox} title={intl('isActive')} />
            </SplitFormRow>
            {renderFields()}
        </FormStack>
    );
};

export default React.memo(SupportFields);
